import React, { useState, useEffect, useCallback, memo, useMemo } from 'react';
import { 
  Search, 
  Music, 
  Play, 
  Heart, 
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Plus,
  Loader,
  Check
} from 'lucide-react';
import PlaylistSelector from './PlaylistSelector';
import dataManager from '../utils/dataManager';

// 工具函数：格式化时长
const formatDuration = (duration) => {
  if (!duration) return '--:--';
  const totalSeconds = Math.floor(duration);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// 获取平台信息
const getPlatformInfo = (platform) => {
  const platformInfo = {
    netease: { 
      icon: <img src="/icon/netease.png" alt="网易云音乐" className="w-6 h-6" />
    },
    kuwo: { 
      icon: <img src="/icon/kw.png" alt="酷我音乐" className="w-6 h-6" />
    },
    qq: { 
      icon: <img src="/icon/qq.png" alt="QQ音乐" className="w-6 h-6" />
    }
  };
  
  return platformInfo[platform] || { 
    icon: (
      <Music className="w-6 h-6 text-gray-600" />
    )
  };
};

// 自定义平台下拉选择组件
const PlatformDropdown = memo(({ selectedSource, onSourceChange, supportedPlatforms }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const selectedPlatform = supportedPlatforms[selectedSource];
  
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-dark-600 hover:bg-dark-100/50 transition-all duration-200 min-w-[120px]"
      >
        <span>{selectedPlatform?.name}</span>
        <ChevronDown 
          size={14} 
          className={`text-dark-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-dark-50 rounded-lg shadow-lg border border-dark-200 z-50 min-w-[160px] overflow-hidden">
          {Object.entries(supportedPlatforms).map(([key, platform]) => (
            platform.enabled && (
              <button
                key={key}
                type="button"
                onClick={() => {
                  onSourceChange(key);
                  setIsOpen(false);
                }}
                className="w-full flex items-center justify-between px-4 py-3 text-sm text-dark-800 hover:bg-dark-200 hover:text-primary-600 transition-colors duration-200"
              >
                <span>{platform.name}</span>
                {key === selectedSource && (
                  <Check size={16} className="text-primary-500" />
                )}
              </button>
            )
          ))}
        </div>
      )}
      
      {/* 点击外部关闭下拉框 */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
});

// 优化的搜索结果卡片组件
const SearchResultCard = memo(({ song, index, onPlaySong, onAddToPlaylist, showToastMessage }) => {
  const platformInfo = useMemo(() => getPlatformInfo(song.platform), [song.platform]);
  const [coverImage, setCoverImage] = useState(song.pic || null);
  const [imageLoading, setImageLoading] = useState(!song.pic);
  const [isFavorited, setIsFavorited] = useState(() => dataManager.isFavorite(song.id, song.platform));

  // 检查收藏状态 - 仅在歌曲ID或平台改变时更新
  useEffect(() => {
    setIsFavorited(dataManager.isFavorite(song.id, song.platform));
  }, [song.id, song.platform]);

  // 获取封面图片 - 仅在图片URL改变时更新
  useEffect(() => {
    if (song.pic !== coverImage) {
      setCoverImage(song.pic || null);
      setImageLoading(!song.pic);
    }
  }, [song.pic, coverImage]);

  // 切换收藏状态
  const handleToggleFavorite = useCallback((e) => {
    e.stopPropagation();
    if (isFavorited) {
      dataManager.removeFromFavorites(song.id, song.platform);
      setIsFavorited(false);
      showToastMessage('已从我喜欢的音乐中移除');
    } else {
      dataManager.addToFavorites(song);
      setIsFavorited(true);
      showToastMessage('已添加到我喜欢的音乐');
    }
  }, [isFavorited, song.id, song.platform, song, showToastMessage]);

  const handleAddToPlaylistClick = useCallback((e) => {
    e.stopPropagation();
    onAddToPlaylist(song);
  }, [song, onAddToPlaylist]);

  const handlePlayClick = useCallback(() => {
    onPlaySong && onPlaySong(song);
  }, [song, onPlaySong]);
  
  return (
    <div className="group cursor-pointer">
      {/* 封面图片区域 */}
      <div 
        className="relative aspect-square mb-3 overflow-hidden rounded-xl bg-dark-100 cursor-pointer"
        onClick={handlePlayClick}
      >
        {coverImage && !imageLoading ? (
          <img
            src={coverImage}
            alt={song.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            onError={() => {
              setCoverImage(null);
              setImageLoading(false);
            }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-dark-200 to-dark-300 flex items-center justify-center">
            <Music size={48} className="text-dark-400" />
          </div>
        )}
        
        {/* 播放按钮覆盖层 */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
          <button className="w-12 h-12 bg-gray-900 backdrop-blur-sm rounded-full shadow-lg opacity-0 group-hover:opacity-100 transform scale-90 group-hover:scale-100 transition-all duration-300 flex items-center justify-center hover:scale-110">
            <Play size={16} className="text-white ml-0.5" />
          </button>
        </div>

        {/* 平台标识 */}
        {song.platform && (
          <div className="absolute top-2 right-2 opacity-90">
            <div className="w-10 h-10 bg-white/80 backdrop-blur-md rounded-lg flex items-center justify-center shadow-lg">
              <div className="w-7 h-7 flex items-center justify-center">
                {React.cloneElement(platformInfo.icon, {
                  className: platformInfo.icon.props.src ? "w-7 h-7 object-contain" : "w-7 h-7"
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 歌曲信息 */}
      <div className="px-1">
        <h3 className="font-semibold text-dark-800 truncate mb-1 group-hover:text-primary-600 transition-colors duration-200">
          {song.name}
        </h3>
        <p className="text-sm text-dark-500 truncate mb-1">{song.artist}</p>
        {song.album && (
          <p className="text-xs text-dark-400 truncate">{song.album}</p>
        )}
      </div>

      {/* 底部操作栏 */}
      <div className="flex items-center justify-between px-1 mt-2 opacity-0 group-hover:opacity-100 transition-all duration-300">
        <div className="flex items-center gap-1">
          <button 
            onClick={handleToggleFavorite}
            className="p-1.5 hover:bg-dark-100 rounded-full transition-colors duration-200"
            title={isFavorited ? '取消收藏' : '收藏'}
          >
            <Heart 
              size={14} 
              className={`transition-colors duration-200 ${
                isFavorited 
                  ? 'text-red-500 fill-current' 
                  : 'text-dark-400 hover:text-red-500'
              }`} 
            />
          </button>
          <button 
            onClick={handleAddToPlaylistClick}
            className="p-1.5 hover:bg-dark-100 rounded-full transition-colors duration-200"
            title="添加到歌单"
          >
            <Plus size={14} className="text-dark-400 hover:text-primary-500" />
          </button>
          
        </div>
        <div className="text-xs text-dark-400">
          {song.duration ? formatDuration(song.duration) : ''}
        </div>
      </div>
    </div>
  );
});

const SearchPage = memo(({ searchState, updateSearchState, onPlaylistsChange, onPlaySong, onAddSongsToQueue }) => {
  const {
    keyword,
    selectedSource,
    searchResults,
    isLoading,
    searchPage,
    totalResults,
    hasSearched
  } = searchState;

  // 支持的平台
  const supportedPlatforms = {
    aggregate: { name: '聚合搜索', enabled: true },
    netease: { name: '网易云音乐', enabled: true },
    kuwo: { name: '酷我音乐', enabled: true },
    qq: { name: 'QQ音乐', enabled: true }
  };

  // API基础配置
  const API_BASE = 'https://music-dl.sayqz.com/api/';

  // 执行搜索
  const performSearch = async (page = 1, searchKeyword = null) => {
    const currentKeyword = searchKeyword || keyword;
    if (!currentKeyword.trim()) return;

    updateSearchState({ 
      isLoading: true, 
      searchPage: page,
      ...(searchKeyword && { keyword: searchKeyword }) // 如果提供了新关键词，更新输入框
    });

    try {
      let url;
      const limit = 20;
      
      if (selectedSource === 'aggregate') {
        url = `${API_BASE}?type=aggregateSearch&keyword=${encodeURIComponent(currentKeyword)}&limit=${limit}&page=${page}`;
      } else {
        url = `${API_BASE}?source=${selectedSource}&type=search&keyword=${encodeURIComponent(currentKeyword)}&limit=${limit}&page=${page}`;
      }

      const response = await fetch(url);
      const data = await response.json();

      if (data.code === 200) {
        updateSearchState({
          searchResults: data.data.results || [],
          totalResults: data.data.total || 0,
          hasSearched: true,
          isLoading: false
        });
      } else {
        console.error('搜索失败:', data.message);
        updateSearchState({
          searchResults: [],
          totalResults: 0,
          isLoading: false
        });
      }
    } catch (error) {
      console.error('搜索出错:', error);
      updateSearchState({
        searchResults: [],
        totalResults: 0,
        isLoading: false
      });
    }
  };

  // 处理搜索提交
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    performSearch(1);
  };

  // 处理分页
  const handlePageChange = (newPage) => {
    performSearch(newPage);
  };





  // 添加状态管理
  const [playlistSelectorOpen, setPlaylistSelectorOpen] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const [showToast, setShowToast] = useState(null);

  // 显示提示消息
  const showToastMessage = useCallback((message, type = 'success') => {
    setShowToast({ message, type });
    setTimeout(() => setShowToast(null), 2000);
  }, []);

  // 添加到歌单
  const handleAddToPlaylist = useCallback((song) => {
    setSelectedSong(song);
    setPlaylistSelectorOpen(true);
  }, []);





  return (
    <div className="flex-1 bg-dark-50 overflow-y-auto scrollbar-thin">
      {/* 搜索头部 - 精简设计 */}
      <div className="sticky top-0 bg-dark-50/95 backdrop-blur-lg border-b border-dark-200/50 z-10">
        <div className="px-6 py-4">
          {/* 搜索表单 - 长条集成设计 */}
          <form onSubmit={handleSearchSubmit}>
            <div className="relative max-w-4xl mx-auto">
              {/* 搜索图标 */}
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-dark-400 z-10" size={20} />
              
              {/* 音源下拉选择 */}
              <div className="absolute left-12 top-1/2 transform -translate-y-1/2 z-10">
                <PlatformDropdown
                  selectedSource={selectedSource}
                  onSourceChange={(source) => updateSearchState({ selectedSource: source })}
                  supportedPlatforms={supportedPlatforms}
                />
              </div>

              {/* 分隔线 */}
              <div className="absolute left-40 top-1/2 transform -translate-y-1/2 w-px h-6 bg-dark-300"></div>

              {/* 搜索输入框 */}
              <input
                type="text"
                value={keyword}
                onChange={(e) => updateSearchState({ keyword: e.target.value })}
                placeholder="搜索歌曲、艺术家、专辑..."
                className="w-full pl-44 pr-20 py-3 bg-dark-100/80 backdrop-blur border-0 rounded-xl text-dark-800 placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:bg-dark-100 transition-all duration-300"
              />

              {/* 搜索按钮 */}
              <button
                type="submit"
                disabled={!keyword.trim() || isLoading}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 text-dark-600 hover:text-primary-500 hover:bg-primary-100/50 active:text-primary-600 active:bg-primary-200/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 rounded-lg"
              >
                {isLoading ? (
                  <Loader className="animate-spin" size={18} />
                ) : (
                  <Search size={18} />
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* 主内容区域 */}
      <div className="px-6 py-4">
        {/* 搜索建议 - 未搜索时显示 */}
        {!hasSearched && (
          <div className="space-y-8">
            {/* 欢迎信息 */}
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <Search size={40} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-dark-800 mb-2">发现你喜欢的音乐</h2>
              <p className="text-dark-500 max-w-md mx-auto">
                搜索歌曲、艺术家、专辑，支持多平台聚合搜索
              </p>
            </div>

            {/* 搜索建议 */}
            <div>
              <h3 className="text-lg font-semibold text-dark-700 mb-4">热门搜索</h3>
              <div className="flex flex-wrap gap-3">
                {[
                  '周杰伦', '邓紫棋', '毛不易', '陈奕迅', '林俊杰',
                  '薛之谦', '张杰', '王力宏', '汪苏泷', '许嵩'
                ].map((keyword, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      // 直接执行搜索，无需等待状态更新
                      performSearch(1, keyword);
                    }}
                    className="px-4 py-2 bg-dark-100 hover:bg-dark-200 text-dark-700 rounded-full transition-all duration-200 hover:scale-105"
                  >
                    {keyword}
                  </button>
                ))}
              </div>
            </div>

            {/* 平台介绍 */}
            <div>
              <h3 className="text-lg font-semibold text-dark-700 mb-4">支持的音乐平台</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(supportedPlatforms)
                  .filter(([key, platform]) => platform.enabled && key !== 'aggregate')
                  .map(([key, platform]) => {
                    const platformInfo = getPlatformInfo(key);
                    return (
                      <div key={key} className="bg-dark-100/50 rounded-xl p-4 hover:bg-dark-100 transition-colors duration-200">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-white/60 backdrop-blur-md rounded-xl flex items-center justify-center shadow-sm">
                            <div className="w-8 h-8 flex items-center justify-center">
                              {React.cloneElement(platformInfo.icon, {
                                className: platformInfo.icon.props.src ? "w-8 h-8 object-contain" : "w-8 h-8"
                              })}
                            </div>
                          </div>
                          <div>
                            <h4 className="font-medium text-dark-800">{platform.name}</h4>
                            <p className="text-sm text-dark-500">
                              {key === 'netease' && '海量正版音乐'}
                              {key === 'kuwo' && '高品质音效'}
                              {key === 'qq' && '全民K歌'}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

        {/* 搜索结果 */}
        {hasSearched && (
          <div className="space-y-8">
            {/* 结果统计和筛选 */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-dark-800 mb-1">
                  搜索结果
                </h2>
                {totalResults > 0 && (
                  <p className="text-dark-500">找到 {totalResults} 首歌曲</p>
                )}
              </div>
              {selectedSource === 'aggregate' && searchResults.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-full text-sm">
                  <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                  聚合搜索
                </div>
              )}
            </div>

            {/* 加载状态 */}
            {isLoading && (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader className="animate-spin text-primary-500 mb-4" size={40} />
                <span className="text-dark-500 text-lg">正在搜索音乐...</span>
              </div>
            )}

            {/* 无结果状态 */}
            {!isLoading && searchResults.length === 0 && (
              <div className="text-center py-20">
                <div className="w-24 h-24 bg-dark-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Music size={48} className="text-dark-400" />
                </div>
                <h3 className="text-xl font-semibold text-dark-700 mb-2">未找到相关音乐</h3>
                <p className="text-dark-500 max-w-md mx-auto">
                  请尝试使用不同的关键词或切换到其他音乐平台搜索
                </p>
              </div>
            )}

            {/* 搜索结果网格 */}
            {!isLoading && searchResults.length > 0 && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
                  {searchResults.map((song, index) => (
                    <SearchResultCard
                      key={`${song.platform}-${song.id}`}
                      song={song}
                      index={(searchPage - 1) * 20 + index}
                      onPlaySong={onPlaySong}
                      onAddToPlaylist={handleAddToPlaylist}
                      showToastMessage={showToastMessage}
                    />
                  ))}
                </div>

                {/* 分页控制 */}
                {totalResults > 20 && (
                  <div className="flex items-center justify-center gap-6 pt-8">
                    <button
                      onClick={() => handlePageChange(searchPage - 1)}
                      disabled={searchPage === 1}
                      className="flex items-center gap-2 px-6 py-3 bg-dark-100 text-dark-600 rounded-2xl hover:bg-dark-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-medium"
                    >
                      <ChevronLeft size={18} />
                      上一页
                    </button>
                    
                    <div className="flex items-center gap-2">
                      <span className="text-dark-500 font-medium">第</span>
                      <span className="px-3 py-1 bg-primary-500 text-white rounded-lg font-semibold">
                        {searchPage}
                      </span>
                      <span className="text-dark-500 font-medium">页</span>
                    </div>
                    
                    <button
                      onClick={() => handlePageChange(searchPage + 1)}
                      disabled={searchPage * 20 >= totalResults}
                      className="flex items-center gap-2 px-6 py-3 bg-dark-100 text-dark-600 rounded-2xl hover:bg-dark-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-medium"
                    >
                      下一页
                      <ChevronRight size={18} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* 歌单选择器 */}
        <PlaylistSelector
          isOpen={playlistSelectorOpen}
          onClose={() => setPlaylistSelectorOpen(false)}
          song={selectedSong}
          onSuccess={(playlistName) => {
            showToastMessage(`已添加到「${playlistName}」`);
            if (onPlaylistsChange) {
              onPlaylistsChange(); // 刷新侧边栏歌单列表
            }
          }}
        />

        {/* 提示消息 */}
        {showToast && (
          <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50">
            <div className={`px-6 py-3 rounded-2xl shadow-lg backdrop-blur-md ${
              showToast.type === 'success' 
                ? 'bg-green-500/90 text-white' 
                : 'bg-red-500/90 text-white'
            }`}>
              <p className="font-medium">{showToast.message}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default SearchPage;
